import { useState, type FormEvent } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { Eye, EyeSlash } from '@phosphor-icons/react'
import { ApiError, ErrorCodes, useNetworkStatus } from '@/api'
import { useI18n } from '@/i18n'
import { Banner, BilingualLabel, Button, Card, Field, Input, Logo } from '@/ui'
import { LanguageToggle } from '@/features/phone/components/LanguageToggle'
import { useAuth } from './context'
import { landingPath } from './roles'

/** Only same-app relative paths are accepted as ?next= targets (no open redirect). */
function safeNext(raw: string | null): string | null {
  if (!raw) return null
  return raw.startsWith('/') && !raw.startsWith('//') ? raw : null
}

export function LoginPage() {
  const { t } = useI18n()
  const { login, status, roles, expired } = useAuth()
  const { online } = useNetworkStatus()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const next = safeNext(params.get('next'))
  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [show, setShow] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (status === 'authenticated' && !busy)
    return <Navigate to={next ?? landingPath(roles)} replace />

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      const me = await login(identifier, password)
      navigate(next ?? landingPath(me.roles.map((r) => r.name)), { replace: true })
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.kind === 'network' || err.kind === 'timeout') setError(t('auth.needsNetwork'))
        else if (err.code === ErrorCodes.ACCOUNT_DISABLED) setError(t('auth.inactive'))
        else if (err.kind === 'unauthorized' || err.code === ErrorCodes.INVALID_CREDENTIALS)
          setError(t('auth.invalid'))
        else setError(err.message)
      } else {
        setError(err instanceof Error ? err.message : String(err))
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="flex min-h-dvh flex-col bg-paper">
      <div className="flex items-center justify-between px-4 pt-safe">
        <div className="py-4">
          <Logo />
        </div>
        <LanguageToggle />
      </div>
      <div className="flex flex-1 items-start justify-center px-4 pb-10 pt-6 sm:items-center sm:pt-0">
        <Card className="w-full max-w-sm px-5 py-6">
          <div className="eyebrow mb-2">{t('auth.eyebrow')}</div>
          <h1 className="mb-1 text-[26px] leading-tight">
            <BilingualLabel
              k="auth.signIn"
              layout="stacked"
              secondaryClassName="text-[15px] font-sans"
            />
          </h1>
          <p className="mb-5 text-[14px] text-muted">{t('auth.signInHint')}</p>

          <div className="mb-4 flex flex-col gap-2">
            {expired && <Banner tone="warn">{t('auth.sessionExpired')}</Banner>}
            {!online && <Banner tone="warn">{t('auth.needsNetwork')}</Banner>}
            {error && <Banner tone="error">{error}</Banner>}
          </div>

          <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
            <Field label={<BilingualLabel k="auth.identifier" />} required>
              <Input
                size="touch"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                required
              />
            </Field>
            <Field label={<BilingualLabel k="auth.password" />} required>
              <Input
                size="touch"
                type={show ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                trailing={
                  <button
                    type="button"
                    onClick={() => setShow((s) => !s)}
                    aria-label={show ? t('auth.hidePassword') : t('auth.showPassword')}
                    className="pointer-events-auto rounded-chip p-1 text-[20px] text-muted hover:text-ink"
                  >
                    {show ? <EyeSlash aria-hidden="true" /> : <Eye aria-hidden="true" />}
                  </button>
                }
              />
            </Field>
            <Button
              type="submit"
              size="xl"
              fullWidth
              loading={busy}
              disabled={!identifier.trim() || !password}
            >
              {t('auth.signIn')}
            </Button>
          </form>
        </Card>
      </div>
    </main>
  )
}

export function NoAccessPage() {
  const { t } = useI18n()
  const { logout, user } = useAuth()
  return (
    <main className="flex min-h-dvh items-center justify-center bg-paper px-4">
      <Card className="max-w-sm px-5 py-6 text-center">
        <h1 className="text-[22px]">{t('auth.forbidden')}</h1>
        <p className="mt-2 text-[14px] text-muted">
          {user?.username} · {user?.roles.map((r) => r.name).join(', ') || '—'}
        </p>
        <Button className="mt-5" variant="secondary" onClick={() => void logout()}>
          {t('auth.signOut')}
        </Button>
      </Card>
    </main>
  )
}
